// src/api/pixelSites.ts
/**
 * Frontend API client for Verity Pixel sites (plan 2026-10-02 verity pixel,
 * D11 / step 6.1): the install panel's settings, snippet and live status.
 *
 * Talks to the BFF proxy at `/pixel-sites` (strict pass-through to the
 * backend's `api/routes/pixel_site_routes.py`). Thin wrappers around
 * `apiJson`, which throws `ApiError` (with `.status`) on any non-2xx, so the
 * panel can branch on 503 (no collect host in this environment: an info
 * state), 403 (not an account admin) and 422 (invalid hosts / groups).
 *
 * Types are defined here rather than imported from `@verity/shared-types`:
 * the generated OpenAPI types are regenerated separately.
 */
import { apiJson } from "./client";

/** The site key (`pxs_…`) of a VERITY_PIXEL managed source, from its config. */
export function pixelSiteKeyOf(config: Record<string, unknown> | null | undefined): string | null {
  const key = config?.site_public_id;
  return typeof key === "string" && key ? key : null;
}

export type PixelConsentMode = "auto" | "manual";
export type PixelConsentPurpose = "marketing" | "analytics";
export type PixelSiteStatusValue = "PENDING" | "ACTIVE" | "DISABLED";

export type PixelOneTrustGroups = {
  analytics?: string | null;
  marketing?: string | null;
};

export type PixelSite = {
  site_key: string;
  website_id: string;
  website_host: string;
  status: PixelSiteStatusValue;
  allowed_hosts: string[];
  consent_mode: PixelConsentMode;
  consent_purpose: PixelConsentPurpose;
  onetrust_groups: PixelOneTrustGroups | null;
  /** Minutes after an add-to-cart without a purchase before the cart counts as abandoned. */
  abandoned_cart_minutes: number;
  /** Hosts a cart reminder's product images may come from (exact match), e.g. a CDN. */
  image_hosts: string[];
  snippet: string;
  /** The Shopify storefront (`fnt_…`) this site measures, or null. */
  storefront_id: string | null;
  storefront_name: string | null;
  /** Only on a PATCH that linked a storefront: what installing the web pixel did. */
  web_pixel: WebPixelState | null;
  /**
   * Only on a PATCH: what writing the page-tracking config to the linked
   * store did. A write result, not the embed's state: read that with
   * `getStorefrontEmbed`. Optional for a backend that predates it.
   */
  storefront_embed?: StorefrontEmbedWrite | null;
};

/** What a PATCH did with the page-tracking config on the linked store. */
export type StorefrontEmbedWrite =
  | "written"
  | "other_site"
  | "not_linked"
  | "not_connected"
  | "unavailable"
  | "error";

/**
 * Page tracking on the linked Shopify store: is the Verity Pixel app embed
 * switched on in the live theme, with this site's config (plan 2026-10-09
 * pixel app embed). Mirrors the backend's storefront-embed state.
 */
export type StorefrontEmbedState =
  | "on"
  | "off"
  | "stale_config"
  | "other_site"
  | "not_linked"
  | "not_connected"
  | "reconnect_required"
  | "unavailable"
  | "error";

export type StorefrontEmbedStatus = {
  state: StorefrontEmbedState;
  /** Shopify theme-editor deep link that switches the embed on, or null. */
  activate_url: string | null;
  /** Hosts the store serves that are not in the site's allowed hosts. */
  missing_hosts: string[];
};

/**
 * Verity's web pixel on the linked Shopify store (links checkouts, including
 * express checkouts, to campaigns). Mirrors `WebPixelState` in the backend's
 * `services/verity_pixel/shopify_web_pixel.py`.
 */
export type WebPixelState =
  | "not_linked"
  | "not_connected"
  | "reconnect_required"
  | "unavailable"
  | "not_installed"
  | "installed"
  | "error";

export type PixelSiteUpdate = {
  allowed_hosts?: string[];
  consent_mode?: PixelConsentMode;
  consent_purpose?: PixelConsentPurpose;
  onetrust_groups?: PixelOneTrustGroups | null;
  abandoned_cart_minutes?: number;
  image_hosts?: string[];
  /** A Shopify storefront's id, or null to unlink. Omit to leave the link alone. */
  storefront_id?: string | null;
};

export type PixelInstallState =
  | "waiting_for_first_event"
  | "receiving"
  | "quiet"
  | "paused"
  | "setting_up";

export type PixelSiteStatus = {
  site_key: string;
  state: PixelInstallState;
  first_event_at: string | null;
  last_event_at: string | null;
  /** Newest UTC day the hourly health check saw in the warehouse. */
  warehouse_through: string | null;
  /** False until the account's PII vault is active (gate 3.5). */
  email_identification_available: boolean;
  /** False where the environment has no collect host (no snippet yet). */
  snippet_available: boolean;
};

/**
 * One pixel site: settings + install snippet.
 * @throws ApiError 503 where the environment has no collect host (the message
 * is customer-readable), 404 for an unknown site.
 */
export async function getPixelSite(siteKey: string): Promise<PixelSite> {
  return apiJson<PixelSite>(`/pixel-sites/${encodeURIComponent(siteKey)}`);
}

/**
 * Change a pixel site's settings (account admin only).
 * @throws ApiError 403 for a non-admin, 422 with a customer-readable message
 * for invalid hosts or OneTrust groups.
 */
export async function updatePixelSite(siteKey: string, body: PixelSiteUpdate): Promise<PixelSite> {
  return apiJson<PixelSite>(`/pixel-sites/${encodeURIComponent(siteKey)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** The live install status. Postgres-only on the backend, cheap enough to poll. */
export async function getPixelSiteStatus(siteKey: string): Promise<PixelSiteStatus> {
  return apiJson<PixelSiteStatus>(`/pixel-sites/${encodeURIComponent(siteKey)}/status`);
}

/** Live check of the web pixel on the linked store (one Shopify read; any member). */
export async function getWebPixelState(siteKey: string): Promise<WebPixelState> {
  const res = await apiJson<{ state: WebPixelState }>(
    `/pixel-sites/${encodeURIComponent(siteKey)}/web-pixel`,
  );
  return res.state;
}

/** Install or correct the web pixel on the linked store (account admin only). */
export async function installWebPixel(siteKey: string): Promise<WebPixelState> {
  const res = await apiJson<{ state: WebPixelState }>(
    `/pixel-sites/${encodeURIComponent(siteKey)}/web-pixel`,
    { method: "POST" },
  );
  return res.state;
}

/**
 * Page tracking on the linked store (Shopify reads; any member). `fresh`
 * bypasses the backend's short state cache (the **Check again** action).
 */
export async function getStorefrontEmbed(
  siteKey: string,
  { fresh = false }: { fresh?: boolean } = {},
): Promise<StorefrontEmbedStatus> {
  return apiJson<StorefrontEmbedStatus>(
    `/pixel-sites/${encodeURIComponent(siteKey)}/storefront-embed?fresh=${fresh ? "true" : "false"}`,
  );
}

/** Write the page-tracking config to the linked store again (account admin only). */
export async function repairStorefrontEmbed(siteKey: string): Promise<StorefrontEmbedStatus> {
  return apiJson<StorefrontEmbedStatus>(
    `/pixel-sites/${encodeURIComponent(siteKey)}/storefront-embed`,
    { method: "POST" },
  );
}

/** Why the live count is not available (plan 2026-10-05, section B). */
export type LiveVisitorsReason = "no_pixel" | "setting_up" | "unavailable";

export type LiveVisitorsSite = {
  site_key: string;
  host: string;
  visitors: number;
  /** `visitors` is an estimate from a visitor sample (show "≈"). Optional
   * only for a backend that predates it. */
  sampled?: boolean;
};

/** A page path and its page views in the live window (account-wide). */
export type LiveTopPage = {
  path: string;
  page_views: number;
};

/**
 * A referring host ("google.com") or "direct": the sessions that arrived from
 * it in the live window (the ranking) and its page views. `sessions` is
 * optional only for a backend that predates it.
 */
export type LiveTopSource = {
  source: string;
  sessions?: number;
  page_views: number;
};

/** The two live windows, in seconds: the last 5 or 30 minutes. */
export type LiveWindowSeconds = 300 | 1800;

/** Event counts in the live window. */
export type LiveActivity = {
  page_views: number;
  add_to_carts: number;
  checkouts: number;
  purchases: number;
  sign_ins: number;
};

/**
 * Which figures are estimates from a visitor sample on a high-traffic site
 * (show "≈" on exactly those); all false when every count is exact.
 */
export type LiveSampled = {
  visitors: boolean;
  top_pages: boolean;
  top_sources: boolean;
  activity: (keyof LiveActivity)[];
};

/**
 * Visitors live on the account's sites: distinct visitors with an event in
 * the last `window_s` seconds. `by_site` lists every ACTIVE site of the
 * account, zero included. `truncated` means the backend hit its pull bound
 * and `visitors` is a lower bound (render it with a "+").
 */
export type LiveVisitors = {
  available: boolean;
  reason?: LiveVisitorsReason | null;
  visitors: number;
  by_site: LiveVisitorsSite[];
  truncated: boolean;
  as_of: string | null;
  window_s: number;
  /** At most 5, page views descending; empty until collect sends `page_path`. */
  top_pages: LiveTopPage[];
  /** At most 5, sessions descending (ties by page views). */
  top_sources: LiveTopSource[];
  activity: LiveActivity;
  /** Optional only for a backend that predates it. */
  sampled?: LiveSampled;
};

/**
 * The live visitor count for the current account (any member). Polled every
 * few seconds by `useLiveVisitors`; the backend caches it per account for 5 s.
 * `available=false` is a 200 with a `reason`, never an error.
 */
export async function getLiveVisitors(
  window: LiveWindowSeconds = 300,
): Promise<LiveVisitors> {
  return apiJson<LiveVisitors>(`/pixel-sites/live-visitors?window=${window}`);
}
